/**
 * An email that arrived, as HTML — `docs/activity.md` §7.10 (Aaron, Oct 24,
 * note 2.6: "It should be able to display html email signatures like any web
 * email client").
 *
 * The text of an email is still what a preview, a search and the record read
 * (`emailToText`); this is how it *looked*, kept beside it so the thread can
 * draw it — a signature's logo, its table, its links. Four things, all pure
 * string work so the server and the browser give the same answer:
 *
 *  - `sanitizeEmailHtml` — an allow-list pass over somebody else's markup:
 *    no script, no style that loads anything but an image, no form, no frame,
 *    no event handler, and no URL but `http(s)`, `mailto`, `tel`, an inline
 *    `data:image` and the message's own `cid:` images. Every attribute comes
 *    out re-quoted, so what follows can read it without parsing. **It is
 *    defence in depth, not the boundary**: the thread draws the result only
 *    inside `<iframe sandbox>` with scripts off, which is what makes a
 *    mistake here harmless rather than a hole.
 *  - the **quoted history** — where it starts, as an offset into the output,
 *    found by the same lines `emailToText` cuts at (*On … wrote:*, *From:*,
 *    *-----Original Message-----*, *>*) and by the containers the big mail
 *    clients wrap it in (Gmail's `gmail_quote`, Apple's `blockquote
 *    type="cite"`, Outlook's `divRplyFwdMsg` and its ruled header). The thread
 *    folds it behind "…", as web mail does.
 *  - `hideRemoteImages` — the same HTML with every image from the internet
 *    switched off, for somebody Rowted doesn't know (the card's *Show
 *    images*): a remote image is how a sender learns the mail was opened.
 *  - `htmlToText` — the text, for `emailToText`.
 */
/** The most HTML kept for one email. Past it the thread shows the text. */
export const EMAIL_HTML_MAX = 400_000;
// ---------------------------------------------------------------------------
// Entities
// ---------------------------------------------------------------------------
const NAMED = {
    nbsp: ' ', amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", rsquo: '’', lsquo: '‘', rdquo: '”',
    ldquo: '“', sbquo: '‚', bdquo: '„', ndash: '–', mdash: '—', hellip: '…', copy: '©', reg: '®', trade: '™',
    bull: '•', middot: '·', zwnj: '‌', zwj: '‍', shy: '­', euro: '€', pound: '£', cent: '¢',
    yen: '¥', deg: '°', times: '×', divide: '÷', laquo: '«', raquo: '»', sect: '§', para: '¶', frac12: '½',
    frac14: '¼', frac34: '¾', eacute: 'é', egrave: 'è', aacute: 'á', agrave: 'à', oacute: 'ó', uacute: 'ú',
    iacute: 'í', ntilde: 'ñ', ccedil: 'ç', uuml: 'ü', ouml: 'ö', auml: 'ä', szlig: 'ß', emsp: ' ',
    ensp: ' ', thinsp: ' ', lrm: '‎', rlm: '‏',
};
/** `&amp;`, `&#8217;`, `&#x2019;` and the common named ones — once, left to right. */
export function decodeEntities(text) {
    return text.replace(/&(#\d{1,7}|#[xX][0-9a-fA-F]{1,6}|[a-zA-Z][a-zA-Z0-9]{1,31});?/g, (whole, body) => {
        if (body.startsWith('#')) {
            const code = body[1] === 'x' || body[1] === 'X' ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10);
            if (!Number.isFinite(code) || code <= 0 || code > 0x10ffff || (code >= 0xd800 && code <= 0xdfff))
                return '�';
            return String.fromCodePoint(code);
        }
        return NAMED[body] ?? NAMED[body.toLowerCase()] ?? whole;
    });
}
const escapeAttribute = (value) => value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
// ---------------------------------------------------------------------------
// HTML → text
// ---------------------------------------------------------------------------
const BLOCKS = 'p|div|li|tr|h[1-6]|blockquote|table|ul|ol|pre|section|article|header|footer|center|dl|dt|dd|hr';
const SOFT = '\u0001';
/**
 * An HTML body as lines of text: what a mail client's "plain text" shows.
 * Comments go (Outlook's `<!--[if mso]>` blocks repeat the message for
 * itself), so do the head, styles and scripts; the source's own line breaks
 * are only spaces; a `<br>` is a line and a block starts and ends one.
 */
export function htmlToText(html) {
    const text = html
        .replace(/<!--[\s\S]*?(?:-->|$)/g, '')
        .replace(/<(script|style|title|head|noscript|xml)\b[\s\S]*?<\/\1\s*>/gi, '')
        .replace(/\s+/g, ' ')
        .replace(/<br\b[^>]*>/gi, '\n')
        .replace(new RegExp(`<\\/?(?:${BLOCKS})\\b[^>]*>`, 'gi'), SOFT)
        .replace(/<\/?[a-zA-Z!?][^>]*>/g, '');
    return decodeEntities(text)
        .replace(/[​-‍⁠﻿͏­]/g, '')
        /* A run of block edges is one line break, and none at all straight after a `<br>`: a
           browser draws `<div>Hi<br></div><div>Next</div>` as two lines, not three. */
        .replace(new RegExp(`[ \\t\\u00a0]*${SOFT}[ \\t\\u00a0${SOFT}]*`, 'g'), SOFT)
        .replace(new RegExp(`\\n[ \\t\\u00a0]*${SOFT}`, 'g'), '\n')
        .replace(new RegExp(SOFT, 'g'), '\n')
        .split('\n')
        .map((line) => line.replace(/[ \t ]+/g, ' ').trim())
        .join('\n');
}
// ---------------------------------------------------------------------------
// The sanitiser
// ---------------------------------------------------------------------------
/** Elements kept, with whichever of their attributes are allowed. */
const KEPT = new Set([
    'a', 'abbr', 'address', 'article', 'aside', 'b', 'bdi', 'bdo', 'big', 'blockquote', 'br', 'caption', 'center',
    'cite', 'code', 'col', 'colgroup', 'dd', 'del', 'details', 'dfn', 'div', 'dl', 'dt', 'em', 'figcaption', 'figure',
    'font', 'footer', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'header', 'hr', 'i', 'img', 'ins', 'kbd', 'li', 'main',
    'mark', 'nav', 'ol', 'p', 'pre', 'q', 's', 'samp', 'section', 'small', 'span', 'strike', 'strong', 'style', 'sub',
    'summary', 'sup', 'table', 'tbody', 'td', 'tfoot', 'th', 'thead', 'time', 'tr', 'tt', 'u', 'ul', 'var', 'wbr',
]);
/** Elements that go **with** everything inside them. */
const DROPPED = new Set([
    'script', 'noscript', 'iframe', 'frame', 'frameset', 'object', 'applet', 'template', 'svg', 'math', 'title',
    'textarea', 'select', 'xmp', 'noembed', 'noframes', 'plaintext', 'audio', 'video', 'canvas', 'xml',
]);
const VOID = new Set(['br', 'hr', 'img', 'wbr', 'col', 'area', 'base', 'embed', 'input', 'link', 'meta', 'param',
    'source', 'track', 'keygen']);
const EVERYWHERE = new Set(['align', 'valign', 'bgcolor', 'border', 'class', 'color', 'dir', 'height', 'width', 'id',
    'lang', 'style', 'title', 'face', 'size', 'hspace', 'vspace', 'nowrap']);
const PER_TAG = {
    a: ['href'],
    img: ['src', 'alt'],
    table: ['cellpadding', 'cellspacing', 'summary', 'background', 'frame', 'rules'],
    td: ['colspan', 'rowspan', 'background', 'headers', 'scope', 'abbr'],
    th: ['colspan', 'rowspan', 'background', 'headers', 'scope', 'abbr'],
    tr: ['background'],
    col: ['span'],
    colgroup: ['span'],
    ol: ['start', 'type', 'reversed'],
    ul: ['type'],
    li: ['value', 'type'],
    blockquote: ['cite', 'type'],
    q: ['cite'],
    del: ['cite', 'datetime'],
    ins: ['cite', 'datetime'],
    time: ['datetime'],
    details: ['open'],
};
/** The blocks that start and end a line — where a quoted history can begin. */
const LINE_TAGS = new Set(['p', 'div', 'blockquote', 'table', 'tr', 'li', 'ul', 'ol', 'h1', 'h2', 'h3', 'h4', 'h5',
    'h6', 'hr', 'pre', 'section', 'article', 'header', 'footer', 'center', 'dl', 'dt', 'dd', 'br', 'body']);
/** The lines `emailToText` cuts at, so the text and the drawing fold in one place. */
export const QUOTE_LINES = [
    /^\s*On .+wrote:\s*$/, /^\s*From:\s/, /^\s*-{2,}\s*Original Message\s*-{2,}/i, /^\s*>/,
];
const isQuoteLine = (line) => QUOTE_LINES.some((pattern) => pattern.test(line));
/** The containers the big mail clients put a quoted history in. */
function isQuoteContainer(tag, attrs) {
    const cls = ` ${(attrs.get('class') ?? '').toLowerCase()} `;
    const id = (attrs.get('id') ?? '').toLowerCase();
    if (/\s(gmail_quote|gmail_attr|yahoo_quoted|moz-cite-prefix|protonmail_quote)\s/.test(cls))
        return true;
    if (tag === 'blockquote' && (attrs.get('type') ?? '').toLowerCase() === 'cite')
        return true;
    if (id === 'divrplyfwdmsg' || id === 'appendonsend' || id.startsWith('mail-editor-reference-message-container'))
        return true;
    if (tag === 'hr' && id === 'stopspelling')
        return true;
    /* Outlook on Windows: the ruled block above *From: … Sent: …*. */
    return tag === 'div' && /border-top\s*:\s*solid\s+#(?:e1e1e1|b5c4df)/i.test(attrs.get('style') ?? '');
}
const ATTRIBUTE = /([^\s"'>/=]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+)))?/g;
function attributesOf(source) {
    const out = new Map();
    for (const match of source.matchAll(ATTRIBUTE)) {
        const name = match[1].toLowerCase();
        if (out.has(name))
            continue;
        out.set(name, decodeEntities(match[2] ?? match[3] ?? match[4] ?? ''));
    }
    return out;
}
/** A URL with its control characters and spaces out, as a browser reads the scheme. */
const bareUrl = (url) => url.replace(/[\u0000- \u007f-\u009f]/g, '');
const isWebUrl = (url) => /^https?:\/\//i.test(bareUrl(url));
const isLinkUrl = (url) => /^(https?:\/\/|mailto:|tel:)/i.test(bareUrl(url));
const isDataImage = (url) => /^data:image\/(png|gif|jpe?g|webp|bmp);base64,[a-z0-9+/=\s]*$/i.test(url.trim());
/**
 * A style, made inert: no `@import`, none of the old script-in-CSS forms, and
 * a `url()` only for an image on the web, an inline one or a `cid:`. Every
 * `url()` comes out single-quoted, which is what `hideRemoteImages` reads.
 */
function cleanCss(css, inline) {
    return css
        .replace(/\/\*[\s\S]*?(?:\*\/|$)/g, '')
        .replace(/@import[^;]*;?/gi, '')
        .replace(/expression\s*\(|behaviou?r\s*:|-moz-binding|javascript\s*:|vbscript\s*:/gi, '')
        .replace(/<\/?/g, '')
        .replace(/url\(\s*(['"]?)([^'")]*)\1\s*\)/gi, (_whole, _quote, url) => {
        const target = url.trim();
        const local = /^cid:/i.test(target) ? inline(target.slice(4)) : null;
        if (local)
            return `url('${local}')`;
        if (isWebUrl(target) || isDataImage(target))
            return `url('${target.replace(/'/g, '%27')}')`;
        return 'none';
    });
}
/**
 * Somebody else's HTML, made safe to draw in a sandboxed frame. Null when
 * there is nothing left to draw, or more than `EMAIL_HTML_MAX` of it.
 */
export function sanitizeEmailHtml(raw, options = {}) {
    const used = new Set();
    const inline = (cid) => {
        const name = cid.trim().replace(/^<|>$/g, '');
        const found = options.inline?.(name) ?? null;
        if (found)
            used.add(name);
        return found;
    };
    let out = '';
    let quoteAt = null;
    let lineStart = 0;
    let lineText = '';
    /* The line that just ended: a quoted history starts at it when it reads like one. */
    const endLine = () => {
        if (quoteAt === null && isQuoteLine(lineText))
            quoteAt = lineStart;
        lineText = '';
    };
    /*
     * A tag, its attributes read once and never backtracked into (the lookahead
     * is atomic): a value is quoted only when the quote follows its `=`, as a
     * browser reads it, and may not run past a `<` — so an apostrophe in
     * `face=Arial's` is a character, and one stray quote cannot swallow the
     * rest of the message into an attribute.
     */
    const tag = /<(\/?)([a-zA-Z][a-zA-Z0-9:_-]*)(?=((?:=\s*"[^"<]*"|=\s*'[^'<]*'|[^>"']|["'])*))\3>/y;
    let at = 0;
    while (at < raw.length) {
        const lt = raw.indexOf('<', at);
        const text = lt === -1 ? raw.slice(at) : raw.slice(at, lt);
        if (text) {
            out += text;
            lineText += decodeEntities(text).replace(/\s+/g, ' ');
        }
        if (lt === -1)
            break;
        at = lt;
        if (raw.startsWith('<!--', at)) {
            const end = raw.indexOf('-->', at + 4);
            at = end === -1 ? raw.length : end + 3;
            continue;
        }
        if (raw.startsWith('<!', at) || raw.startsWith('<?', at)) {
            const end = raw.indexOf('>', at);
            at = end === -1 ? raw.length : end + 1;
            continue;
        }
        tag.lastIndex = at;
        const match = tag.exec(raw);
        if (!match) {
            out += '&lt;';
            lineText += '<';
            at += 1;
            continue;
        }
        at = tag.lastIndex;
        const closing = match[1] === '/';
        let name = match[2].toLowerCase();
        if (!closing && DROPPED.has(name)) {
            const end = new RegExp(`</${name}\\s*>`, 'ig');
            end.lastIndex = at;
            const found = end.exec(raw);
            at = found ? end.lastIndex : raw.length;
            continue;
        }
        if (name === 'style') {
            if (closing)
                continue;
            const end = /<\/style\s*>/ig;
            end.lastIndex = at;
            const found = end.exec(raw);
            const css = raw.slice(at, found ? found.index : raw.length);
            at = found ? end.lastIndex : raw.length;
            out += `<style>${cleanCss(css, inline)}</style>`;
            continue;
        }
        if (name === 'body')
            name = 'div';
        const keep = KEPT.has(name);
        const attrs = closing ? new Map() : attributesOf(match[3] ?? '');
        if (LINE_TAGS.has(name)) {
            endLine();
            if (!closing) {
                lineStart = out.length;
                if (quoteAt === null && keep && isQuoteContainer(name, attrs))
                    quoteAt = out.length;
            }
        }
        if (!keep) {
            if (LINE_TAGS.has(name))
                lineStart = out.length;
            continue;
        }
        if (closing) {
            if (!VOID.has(name))
                out += `</${name}>`;
            if (LINE_TAGS.has(name))
                lineStart = out.length;
            continue;
        }
        const allowed = new Set([...EVERYWHERE, ...(PER_TAG[name] ?? [])]);
        let written = '';
        let dropTag = false;
        for (const [key, value] of attrs) {
            if (!allowed.has(key))
                continue;
            let next = value;
            if (key === 'style')
                next = cleanCss(value, inline);
            else if (key === 'href')
                next = isLinkUrl(value) ? bareUrl(value) : null;
            else if (key === 'background' || key === 'cite')
                next = isWebUrl(value) ? bareUrl(value) : null;
            else if (key === 'src') {
                const target = bareUrl(value);
                if (/^cid:/i.test(target))
                    next = inline(target.slice(4));
                else
                    next = isWebUrl(target) || isDataImage(value) ? (isDataImage(value) ? value.trim() : target) : null;
                /* An image with nothing to draw is a broken box: it goes. */
                if (next === null)
                    dropTag = true;
            }
            if (next === null)
                continue;
            written += ` ${key}="${escapeAttribute(next)}"`;
        }
        if (dropTag)
            continue;
        if (name === 'a')
            written += ' target="_blank" rel="noopener noreferrer"';
        out += `<${name}${written}>`;
        if (name === 'br')
            lineStart = out.length;
        if (name === 'img')
            lineText += ' ';
    }
    endLine();
    if (!/\S/.test(htmlToText(out)) && !/<img\b/i.test(out))
        return null;
    if (out.length > EMAIL_HTML_MAX)
        return null;
    if (quoteAt !== null) {
        const before = out.slice(0, quoteAt);
        if (quoteAt <= 0 || (!/\S/.test(htmlToText(before)) && !/<img\b/i.test(before)))
            quoteAt = null;
    }
    return { html: out, quoteAt, inline: [...used] };
}
/**
 * The same HTML with every image from the internet switched off — an `<img>`
 * keeps its place and its `alt` text, a background goes — and how many there
 * were. Reads `sanitizeEmailHtml`'s output, whose attributes are always
 * double-quoted and whose `url()`s are always single-quoted.
 */
export function hideRemoteImages(html) {
    let hidden = 0;
    const out = html
        .replace(/<img\b[^>]*>/gi, (tag) => tag.replace(/\ssrc="(https?:[^"]*)"/i, (_whole, url) => {
        hidden += 1;
        return ` data-rowted-src="${url}"`;
    }))
        .replace(/\sbackground="https?:[^"]*"/gi, () => {
        hidden += 1;
        return '';
    })
        .replace(/url\('https?:[^']*'\)/gi, () => {
        hidden += 1;
        return 'none';
    });
    return { html: out, hidden };
}
//# sourceMappingURL=email-html.js.map